import { useTranslation } from 'react-i18next';
import useTargetListStyle from '../../../../components/TargetList/style';
import React, {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Stack,
  Text,
  SearchInput,
  Tree,
  TreeItem,
  TreeItemContent,
  Flex,
  TreeItemToggle,
  TreeItemToggleIcon,
  Checkbox,
  MenuButton,
  MenuList,
  MenuItem,
  Menu,
} from '@tonic-one/react';
import {
  GroupNode,
  PlaceHolderNode,
  PocNodeType,
  PocTargetSearchType,
  UserNode,
} from './type';

const SearchModeMenu = forwardRef<{ mode: PocTargetSearchType }>((_, ref) => {
  const { t } = useTranslation('provision');
  const [searchMode, setSearchMode] = useState<PocTargetSearchType>(
    PocTargetSearchType.TargetUser
  );

  useImperativeHandle(ref, () => {
    return {
      mode: searchMode,
    };
  }, [searchMode]);

  return (
    <Menu width="100%">
      <MenuButton variant="secondary">
        <Text>
          {t(
            `provision:provision.service.exchange.poc.searchMode.${searchMode}`
          )}
        </Text>
      </MenuButton>
      <MenuList width="max-content">
        {[PocTargetSearchType.TargetUser, PocTargetSearchType.TargetGroup].map(
          (key) => (
            <MenuItem
              key={key}
              onClick={() => {
                setSearchMode(key);
              }}
            >
              {t(`provision:provision.service.exchange.poc.searchMode.${key}`)}
            </MenuItem>
          )
        )}
      </MenuList>
    </Menu>
  );
});

interface UserTreeItemProps {
  node: UserNode;
  toggleNodeChecked: (key: string) => void;
}
const UserTreeItem: React.FC<UserTreeItemProps> = ({
  node,
  toggleNodeChecked,
}) => {
  return (
    <TreeItem
      key={node.key}
      nodeId={node.key}
      render={() => (
        <TreeItemContent gap="2x">
          <Checkbox
            checked={node.checked}
            onChange={() => {
              toggleNodeChecked(node.key);
            }}
          >
            <Text wordBreak="break-all">{node.title}</Text>
          </Checkbox>
        </TreeItemContent>
      )}
    ></TreeItem>
  );
};

interface GroupTreeItemProps {
  node: GroupNode;
  toggleNodeChecked: (key: string) => void;
  toggleNodeExpanded: (key: string) => void;
}
const GroupTreeItem: React.FC<GroupTreeItemProps> = ({
  node,
  toggleNodeChecked,
  toggleNodeExpanded,
}) => {
  return (
    <TreeItem
      key={node.key}
      nodeId={node.key}
      render={() => (
        <TreeItemContent>
          <Flex flex="none" width="6x">
            <TreeItemToggle
              onClick={() => {
                toggleNodeExpanded(node.key);
              }}
            >
              <TreeItemToggleIcon />
            </TreeItemToggle>
          </Flex>
          <Text wordBreak="break-all">{node.title}</Text>
        </TreeItemContent>
      )}
    >
      {node.children.map((child) => {
        return (
          <UserTreeItem
            key={child.key}
            toggleNodeChecked={toggleNodeChecked}
            node={child}
          />
        );
      })}
    </TreeItem>
  );
};

interface CommonTreeItemProps {
  node: GroupNode | UserNode;
  toggleNodeChecked: (key: string) => void;
  toggleNodeExpanded: (key: string) => void;
}
const CommonTreeItem: React.FC<CommonTreeItemProps> = ({
  node,
  toggleNodeChecked,
  toggleNodeExpanded,
}) => {
  if (node.type === PocNodeType.User) {
    return <UserTreeItem {...{ node, toggleNodeChecked }} />;
  } else {
    return (
      <GroupTreeItem {...{ node, toggleNodeChecked, toggleNodeExpanded }} />
    );
  }
};

interface PocLeftTransferBoxProps {
  data: PlaceHolderNode[];
  toggleNodeChecked: (key: string) => void;
  toggleNodeExpanded: (key: string) => void;
  onSearch: (keyword: string, mode: PocTargetSearchType) => Promise<void>;
}

export const PocLeftTransferBox = memo(
  ({
    data,
    toggleNodeChecked,
    toggleNodeExpanded,
    onSearch,
  }: PocLeftTransferBoxProps) => {
    const { t } = useTranslation('provision');
    const { flexListStyle } = useTargetListStyle();

    // search menu ref
    const modeRef = useRef<{ mode: PocTargetSearchType }>({
      mode: PocTargetSearchType.TargetUser,
    });

    // search input ref
    const ref = useRef<HTMLInputElement>();

    const clearSearchInput = useCallback(() => {
      (ref.current as HTMLInputElement).value = '';
    }, []);

    const expandedKeys = useMemo(() => {
      const keys: string[] = [];
      data.forEach((node) => {
        if (node.expanded) keys.push(node.key);

        node.children.forEach((child) => {
          if (child.type === PocNodeType.Group && child.expanded) {
            keys.push(child.key);
          }
        });
      });
      return keys;
    }, [data]);

    return (
      <Stack flex="1" style={flexListStyle} gap="12px">
        <Text>
          {t('provision:provision.service.exchange.poc.availableTargets')}
        </Text>
        <SearchModeMenu ref={modeRef} />
        <SearchInput
          ref={ref}
          placeholder={t('policy.placeholder.selectedTargets.items')}
          onClearInput={clearSearchInput}
          onKeyDown={(e) => {
            if (e.code === 'Enter') {
              const keyword: string = (e.target as any).value.trim();
              // requestAnimationFrame(clearSearchInput);
              clearSearchInput();
              if (keyword) {
                onSearch(keyword, modeRef.current.mode);
              }
            }
          }}
          width="100%"
          inputProps={{
            textOverflow: 'ellipsis',
          }}
        />
        <Tree expanded={expandedKeys}>
          {data.map((node) => (
            <TreeItem
              key={node.key}
              nodeId={node.key}
              render={() => (
                <TreeItemContent>
                  <Flex flex="none" width="6x">
                    <TreeItemToggle
                      onClick={() => {
                        toggleNodeExpanded(node.key);
                      }}
                    >
                      <TreeItemToggleIcon />
                    </TreeItemToggle>
                  </Flex>
                  <Text wordBreak="break-all">{node.title}</Text>
                </TreeItemContent>
              )}
            >
              {node.children.map((child) => (
                <CommonTreeItem
                  key={child.key}
                  node={child}
                  toggleNodeChecked={toggleNodeChecked}
                  toggleNodeExpanded={toggleNodeExpanded}
                />
              ))}
            </TreeItem>
          ))}
        </Tree>
      </Stack>
    );
  }
);
